import { useRef, useState } from "react";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

import { LoginGrantConfirm } from "pages/Login/LoginGrantConfirm";
import {
  SESSION_ENDED_COPY,
  type GrantLookup,
} from "pages/Login/login-grant-flow";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import {
  differentSurfaceOf,
  leadFlowEvents,
  type ConfirmSurface,
  type SignInSurface,
} from "pages/StartLead/events";
import {
  completionResultOf,
  signInFailureCopy,
  startResultOf,
} from "pages/StartLead/signIn";
import { startSignIn } from "pages/StartLead/signInClient";
import { LOOK_UP_COPY } from "pages/QuoteFlow/interstitial-copy";
import {
  CallButton,
  ProofOutcomeView,
  ReentryFrame,
} from "pages/QuoteFlow/ReentryParts";
import { TypedForm } from "pages/QuoteFlow/ReentryChoice";

const SURFACE: SignInSurface = "verify";
type Phase =
  | { kind: "choose" }
  | { kind: "code"; loginGrant: string; lookup: GrantLookup }
  | { kind: "other_channel" };

/**
 * The verify page's way back in when no code can be produced for its
 * submission: the visitor types the email or phone they used and proves it.
 * The proof signs them in and returns to `redirect`, the page they are on,
 * which then runs its normal check. A proof that reaches nobody is reported
 * through `onNotFound`.
 */
export default function LookUp({
  redirect,
  line,
  onNotFound,
  onSignedIn,
  surface = "verify_look_up",
}: {
  /** The page to come back to once signed in, or undefined for the default. */
  redirect: string | undefined;
  line: { href: string; label: string };
  onNotFound: () => void;
  /** Called as soon as a texted code has signed the visitor in. */
  onSignedIn?: () => void;
  /** The screen this look-up stands on, reported with what happens on it. */
  surface?: ConfirmSurface;
}) {
  const trackEvent = useTrackLeadFlowEvent();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: "choose" });
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const callSurface = surface === "verify_look_up" ? "verify" : surface;
  const typed = useRef("");
  const [prefill, setPrefill] = useState("");

  const backToChoice = (
    message: string | null = null,
    identifier: string = "",
  ) => {
    setNotice(message);
    setPrefill(identifier);
    setPhase({ kind: "choose" });
  };

  const chooseDifferent = () => {
    trackEvent(leadFlowEvents.useDifferentClicked(differentSurfaceOf(surface)));
    backToChoice();
  };

  const submit = async (identifier: string) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    typed.current = identifier;
    trackEvent(leadFlowEvents.signInStarted("look_up", SURFACE));
    const answer = await startSignIn(identifier, redirect);
    setBusy(false);
    trackEvent(
      leadFlowEvents.signInResult("look_up", SURFACE, startResultOf(answer)),
    );
    if (answer.kind === "failed") {
      setNotice(signInFailureCopy(answer.failure));
      return;
    }
    setPhase({
      kind: "code",
      loginGrant: answer.loginGrant,
      lookup: answer.lookup,
    });
  };

  return (
    <ReentryFrame>
      {phase.kind === "code" && (
        <div className="w-full text-left">
          <LoginGrantConfirm
            key={phase.loginGrant}
            grant={{ loginGrant: phase.loginGrant }}
            lookup={phase.lookup}
            surface={surface}
            embedded
            onInvalid={(stage) => {
              trackEvent(
                leadFlowEvents.sessionEndedViewed({
                  purpose: phase.lookup.purpose,
                  surface,
                  stage,
                  resolution: "look_up",
                }),
              );
              backToChoice(SESSION_ENDED_COPY, typed.current);
            }}
            onChangeDestination={chooseDifferent}
            onSignedIn={onSignedIn}
            onCompletion={(completion) => {
              if (completion.kind === "use_account_channel") {
                trackEvent(
                  leadFlowEvents.signInResult(
                    "look_up",
                    SURFACE,
                    completionResultOf(completion),
                  ),
                );
                setPhase({ kind: "other_channel" });
                return;
              }
              if (completion.kind === "create_account") {
                navigate("/start", {
                  replace: true,
                  state: { signupToken: completion.signupToken },
                });
                return;
              }
              onNotFound();
            }}
          />
        </div>
      )}
      {phase.kind === "other_channel" && (
        <>
          <ProofOutcomeView
            outcome={{ kind: "use_account_channel" }}
            onRetry={chooseDifferent}
          />
          <CallButton
            line={line}
            track={{ surface: callSurface, screen: "look_up" }}
          />
        </>
      )}
      {phase.kind === "choose" && (
        <>
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-oat">
            <PhoneIcon
              className="h-5 w-5 text-evergreen"
              strokeWidth={2}
              aria-hidden="true"
            />
          </div>
          <h1 className="font-serif text-[25px] leading-tight text-soil sm:text-[31px]">
            {LOOK_UP_COPY.title}
          </h1>
          <p className="text-base leading-relaxed text-oat-700 sm:text-lg">
            {LOOK_UP_COPY.body}
          </p>
          <TypedForm
            notice={notice}
            initialValue={prefill}
            busy={busy}
            onSubmit={(identifier) => void submit(identifier)}
          />
          <div className="flex flex-col items-center gap-3">
            <CallButton
              line={line}
              track={{ surface: callSurface, screen: "look_up" }}
            />
          </div>
        </>
      )}
    </ReentryFrame>
  );
}
